import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { useEmbaUserContext } from '@/routes/_embaUserLayout/-context/EmbaUser';
import { useSidebar } from '@/routes/_embaUserLayout/user-attendances/$userAttendanceSlug/_lessonSideBarLayout';
import { useSuspenseQuery } from '@apollo/client';
import { createLazyFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { COURSE_LESSON_DOCUMENT_FRAGMENT } from './-CourseLessonDocumentPage/-container';
import { CourseLessonDocumentContainer } from './-CourseLessonDocumentPage/-container';
import { CourseLessonGoalDialogContainer } from './-CourseLessonGoalDialogPage/-container';
import type { COURSE_LESSON_GOAL_SETTING_FRAGMENT } from './-CourseLessonGoalSettingPage';
import { CourseLessonGoalSettingContainer } from './-CourseLessonGoalSettingPage/-container';
import type { COURSE_LESSON_MOVIE_FRAGMENT } from './-CourseLessonMoviePage/-container';
import { CourseLessonMovieContainer } from './-CourseLessonMoviePage/-container';
import type { COURSE_LESSON_PRACTICE_FRAGMENT } from './-CourseLessonPracticePage';
import { CourseLessonPracticeContainer } from './-CourseLessonPracticePage/-container';
import { CourseLessonReflectionContainer } from './-CourseLessonReflectionPage/-container';
import { CourseLessonRolePlayContainer } from './-CourseLessonRolePlayPage/-container';
import type { COURSE_LESSON_SCENARIO_FRAGMENT } from './-CourseLessonScenarioPage/-container';
import { CourseLessonScenarioContainer } from './-CourseLessonScenarioPage/-container';
import type { COURSE_LESSON_SELF_SUMMARIZING_FRAGMENT } from './-CourseLessonSelfSummarizingPage';
import { CourseLessonSelfSummarizingContainer } from './-CourseLessonSelfSummarizingPage/-container';
import { useCourseLessonProgressMutations } from './-hooks/useCourseLessonProgressMutations';

export const Route = createLazyFileRoute(
  '/_embaUserLayout/user-attendances/$userAttendanceSlug/_lessonSideBarLayout/lessons/$lessonSlug/',
)({
  component: component,
});

export const GET_COURSE_LESSON = graphql(/* GraphQL */ `
  query EmbaCourseLesson(
    $embaUserAttendanceSlug: String!
    $embaCourseLessonSlug: String!
  ) {
    embaCourseLesson(
      embaUserAttendanceSlug: $embaUserAttendanceSlug
      embaCourseLessonSlug: $embaCourseLessonSlug
    ) {
      ...EmbaCourseLessonDetail
    }
    embaUserAttendance(
      embaUserAttendanceSlug: $embaUserAttendanceSlug
    ) {
      embaCourse {
        name
      }
    }
  }
`);

export const COURSE_LESSON_DETAIL_FRAGMENT = graphql(/* GraphQL */ `
  fragment EmbaCourseLessonDetail on CourseLesson {
    id
    slug
    name
    lessonContentType
    embaCourseChapter {
      name
    }
    lessonContent {
      ... on CourseLessonScenario {
        id
        ...EmbaCourseLessonScenario
      }
      ... on CourseLessonMovie {
        id
        ...EmbaCourseLessonMovie
      }
      ... on CourseLessonPractice {
        id
        ...EmbaCourseLessonPractice
      }
      ... on CourseLessonDocument {
        id
        ...EmbaCourseLessonDocument
      }
      ... on CourseLessonGoalSetting {
        id
        ...EmbaCourseLessonGoalSetting
      }
      ... on CourseLessonSelfSummarizing {
        id
        ...EmbaCourseLessonSelfSummarizing
      }
      ... on CourseLessonRolePlay {
        id
        ...EmbaCourseLessonRolePlay
      }
      ... on CourseLessonGoalDialog {
        id
        ...EmbaCourseLessonGoalDialog
      }
      ... on CourseLessonReflection {
        id
        ...EmbaCourseLessonReflection
      }
    }
  }
`);

function component() {
  const { showSidebar } = useSidebar();
  const { userAttendanceSlug, lessonSlug } = Route.useParams();

  const { error, data } = useSuspenseQuery(GET_COURSE_LESSON, {
    variables: {
      embaUserAttendanceSlug: userAttendanceSlug,
      embaCourseLessonSlug: lessonSlug,
    },
  });

  if (error) {
    throw error;
  }

  const embaCourseLessonData = useFragment(
    COURSE_LESSON_DETAIL_FRAGMENT,
    data.embaCourseLesson,
  );

  const {
    onEmbaStartCourseLesson,
    startCourseLessonError,
    startCourseLessonLoading,
    onEmbaCompleteCourseLesson,
    completeCourseLessonError,
    completeCourseLessonLoading,
  } = useCourseLessonProgressMutations(
    userAttendanceSlug,
    lessonSlug,
    embaCourseLessonData.lessonContentType,
  );

  if (startCourseLessonError) {
    throw startCourseLessonError;
  }

  if (completeCourseLessonError) {
    throw completeCourseLessonError;
  }

  const { embaUserlastName } = useEmbaUserContext();

  useEffect(() => {
    showSidebar();
  }, [showSidebar]);

  // Suspense と Helmet を併用して、head の title を設定すると適用されない問題があるため、各子Container内で設定しています。
  // React 19 では、Helmetが不要になり title タグを直接設定できるようになるので、その際にここでまとめて設定できるか検討。
  const headTitle = data.embaUserAttendance.embaCourse.name;

  switch (embaCourseLessonData.lessonContentType) {
    case 'movie':
      return (
        <CourseLessonMovieContainer
          key={embaCourseLessonData.id}
          content={
            embaCourseLessonData.lessonContent as FragmentType<
              typeof COURSE_LESSON_MOVIE_FRAGMENT
            >
          }
          lessonName={embaCourseLessonData.name}
          chapterName={embaCourseLessonData.embaCourseChapter.name}
          userAttendanceSlug={userAttendanceSlug}
          onEmbaStartCourseLesson={onEmbaStartCourseLesson}
          onEmbaCompleteCourseLesson={onEmbaCompleteCourseLesson}
          headTitle={headTitle}
        />
      );
    case 'scenario':
      return (
        <CourseLessonScenarioContainer
          key={embaCourseLessonData.lessonContent.id}
          embaUserlastName={embaUserlastName}
          content={
            embaCourseLessonData.lessonContent as FragmentType<
              typeof COURSE_LESSON_SCENARIO_FRAGMENT
            >
          }
          lessonName={embaCourseLessonData.name}
          chapterName={embaCourseLessonData.embaCourseChapter.name}
          onEmbaStartCourseLesson={onEmbaStartCourseLesson}
          onEmbaCompleteCourseLesson={onEmbaCompleteCourseLesson}
          headTitle={headTitle}
        />
      );
    case 'practice':
      return (
        <CourseLessonPracticeContainer
          key={embaCourseLessonData.lessonContent.id}
          content={
            embaCourseLessonData.lessonContent as FragmentType<
              typeof COURSE_LESSON_PRACTICE_FRAGMENT
            >
          }
          lessonName={embaCourseLessonData.name}
          chapterName={embaCourseLessonData.embaCourseChapter.name}
          userAttendanceSlug={userAttendanceSlug}
          lessonSlug={lessonSlug}
          onEmbaStartCourseLesson={onEmbaStartCourseLesson}
          onEmbaCompleteCourseLesson={onEmbaCompleteCourseLesson}
          headTitle={headTitle}
        />
      );
    case 'document':
      return (
        <CourseLessonDocumentContainer
          key={embaCourseLessonData.lessonContent.id}
          content={
            embaCourseLessonData.lessonContent as FragmentType<
              typeof COURSE_LESSON_DOCUMENT_FRAGMENT
            >
          }
          lessonName={embaCourseLessonData.name}
          chapterName={embaCourseLessonData.embaCourseChapter.name}
          userAttendanceSlug={userAttendanceSlug}
          onEmbaStartCourseLesson={onEmbaStartCourseLesson}
          onEmbaCompleteCourseLesson={onEmbaCompleteCourseLesson}
          isLoading={startCourseLessonLoading || completeCourseLessonLoading}
          headTitle={headTitle}
        />
      );
    case 'goal_setting':
      return (
        <CourseLessonGoalSettingContainer
          key={embaCourseLessonData.lessonContent.id}
          content={
            embaCourseLessonData.lessonContent as FragmentType<
              typeof COURSE_LESSON_GOAL_SETTING_FRAGMENT
            >
          }
          lessonName={embaCourseLessonData.name}
          chapterName={embaCourseLessonData.embaCourseChapter.name}
          userAttendanceSlug={userAttendanceSlug}
          lessonSlug={lessonSlug}
          onEmbaStartCourseLesson={onEmbaStartCourseLesson}
          onEmbaCompleteCourseLesson={onEmbaCompleteCourseLesson}
          headTitle={headTitle}
        />
      );
    case 'self_summarizing':
      return (
        <CourseLessonSelfSummarizingContainer
          key={embaCourseLessonData.lessonContent.id}
          content={
            embaCourseLessonData.lessonContent as FragmentType<
              typeof COURSE_LESSON_SELF_SUMMARIZING_FRAGMENT
            >
          }
          lessonName={embaCourseLessonData.name}
          chapterName={embaCourseLessonData.embaCourseChapter.name}
          userAttendanceSlug={userAttendanceSlug}
          lessonSlug={lessonSlug}
          onEmbaStartCourseLesson={onEmbaStartCourseLesson}
          onEmbaCompleteCourseLesson={onEmbaCompleteCourseLesson}
          headTitle={headTitle}
        />
      );
    case 'role_play':
      return (
        <CourseLessonRolePlayContainer
          key={embaCourseLessonData.lessonContent.id}
          lessonName={embaCourseLessonData.name}
          chapterName={embaCourseLessonData.embaCourseChapter.name}
          userAttendanceSlug={userAttendanceSlug}
          lessonSlug={lessonSlug}
          onEmbaStartCourseLesson={onEmbaStartCourseLesson}
          headTitle={headTitle}
        />
      );
    case 'goal_dialog':
      return (
        <CourseLessonGoalDialogContainer
          key={embaCourseLessonData.lessonContent.id}
          lessonName={embaCourseLessonData.name}
          chapterName={embaCourseLessonData.embaCourseChapter.name}
          userAttendanceSlug={userAttendanceSlug}
          lessonSlug={lessonSlug}
          onEmbaStartCourseLesson={onEmbaStartCourseLesson}
          headTitle={headTitle}
        />
      );
    case 'reflection':
      return (
        <CourseLessonReflectionContainer
          key={embaCourseLessonData.lessonContent.id}
          lessonName={embaCourseLessonData.name}
          chapterName={embaCourseLessonData.embaCourseChapter.name}
          userAttendanceSlug={userAttendanceSlug}
          lessonSlug={lessonSlug}
          onEmbaStartCourseLesson={onEmbaStartCourseLesson}
          headTitle={headTitle}
        />
      );
    default:
      throw new Error('Unknown lesson type');
  }
}
